/**
 * Gamification Configuration v1.0
 * 
 * Addictive elements to keep users engaged:
 * - XP & Leveling system
 * - Achievement badges
 * - Daily challenges & streaks
 * - Progress milestones
 * - Surprise rewards
 * - Social proof elements
 * - FOMO triggers
 */

// ==================== CONFIGURATION TYPES ====================

export interface GamificationConfig {
    // Master toggle
    enabled: boolean;

    // XP Earning Rates
    xpRates: {
        sendMessage: number;
        receiveResponse: number;
        askComplexQuestion: number;
        completeQuiz: number;
        passQuiz: number;
        aceQuiz: number;
        completeExam: number;
        passExam: number;
        dailyLogin: number;
        streakDay: number;
        studySession15min: number;
        studySession30min: number;
        studySession60min: number;
        referFriend: number;
    };

    // Level Configuration
    levelConfig: {
        maxLevel: number;
        xpScalingFactor: number; // How much XP increases per level
        tokenBonusPerLevel: number;
    };

    // Streak Settings
    streakConfig: {
        enabled: boolean;
        dailyBonusBase: number; // Base tokens per day
        dailyBonusIncrement: number; // Extra tokens per streak day
        maxDailyBonus: number;
        freezeEnabled: boolean;
        freezeCost: number; // Cost in tokens to freeze streak
        expirationHours: number;
    };

    // Achievement Settings
    achievementConfig: {
        enabled: boolean;
        tokenMultiplier: number; // Multiplier for achievement token rewards
        xpMultiplier: number;
        showCelebration: boolean;
        celebrationDuration: number; // ms
    };

    // Daily Challenge Settings
    dailyChallengeConfig: {
        enabled: boolean;
        challengesPerDay: number;
        easyRewardTokens: number;
        mediumRewardTokens: number;
        hardRewardTokens: number;
        completionBonus: number; // Bonus for completing all daily challenges
        resetHour: number; // Hour to reset (0-23)
    };

    // Challenges Pool
    challengesPool: DailyChallenge[];

    // Surprise Rewards
    surpriseRewardConfig: {
        enabled: boolean;
        baseChance: number; // 0-100
        minTokens: number;
        maxTokens: number;
        cooldownMinutes: number;
    };

    // FOMO Elements
    fomoConfig: {
        enabled: boolean;
        showOnlineCount: boolean;
        showRecentActivity: boolean;
        showFlashSales: boolean;
        streakWarningHours: number;
    };

    // Tier Access
    tierAccess: {
        standard: boolean;
        premium: boolean;
        max: boolean;
    };
}

// ==================== XP & LEVELING SYSTEM ====================

export interface LevelConfig {
    level: number;
    title: string;
    xpRequired: number;
    badge: string;
    color: string;
    perks: string[];
    tokenBonus: number; // Bonus tokens on level up
}

export const LEVEL_CONFIGS: LevelConfig[] = [
    // Beginner Tier (1-5)
    { level: 1, title: 'Freshman', xpRequired: 0, badge: '🌱', color: '#94A3B8', perks: ['Basic access'], tokenBonus: 0 },
    { level: 2, title: 'Sophomore', xpRequired: 100, badge: '📚', color: '#10B981', perks: ['Unlocked: Quiz Mode'], tokenBonus: 50 },
    { level: 3, title: 'Junior', xpRequired: 300, badge: '📖', color: '#3B82F6', perks: ['Unlocked: Detailed Mode'], tokenBonus: 75 },
    { level: 4, title: 'Senior', xpRequired: 600, badge: '🎓', color: '#8B5CF6', perks: ['5% token discount'], tokenBonus: 100 },
    { level: 5, title: 'Graduate', xpRequired: 1000, badge: '👨‍🎓', color: '#F59E0B', perks: ['10% token discount', 'Unlock: Midnight Forest bg'], tokenBonus: 150 },
    // Intermediate Tier (6-10)
    { level: 6, title: 'Professional', xpRequired: 1500, badge: '💼', color: '#EF4444', perks: ['Priority responses'], tokenBonus: 200 },
    { level: 7, title: 'Expert', xpRequired: 2200, badge: '🏆', color: '#EC4899', perks: ['15% token discount'], tokenBonus: 300 },
    { level: 8, title: 'Master', xpRequired: 3000, badge: '👑', color: '#F472B6', perks: ['Exclusive badge', 'Unlock: Synthwave Grid bg'], tokenBonus: 400 },
    { level: 9, title: 'Grand Master', xpRequired: 4000, badge: '⭐', color: '#FBBF24', perks: ['20% token discount'], tokenBonus: 500 },
    { level: 10, title: 'Legend', xpRequired: 5500, badge: '🌟', color: '#F59E0B', perks: ['Legend status', 'Unlock: Aurora Borealis bg'], tokenBonus: 1000 },
    // Advanced Tier (11-15)
    { level: 11, title: 'Virtuoso', xpRequired: 7500, badge: '🔮', color: '#7C3AED', perks: ['25% token discount', 'Unlock: Royal Velvet bg'], tokenBonus: 1200 },
    { level: 12, title: 'Sage', xpRequired: 10000, badge: '🧙', color: '#2563EB', perks: ['Sage badge', 'Extended session memory'], tokenBonus: 1500 },
    { level: 13, title: 'Prodigy', xpRequired: 13000, badge: '💫', color: '#059669', perks: ['30% token discount', 'Unlock: Cherry Blossom bg'], tokenBonus: 1800 },
    { level: 14, title: 'Luminary', xpRequired: 17000, badge: '✨', color: '#D97706', perks: ['Luminary badge', 'Priority queue'], tokenBonus: 2000 },
    { level: 15, title: 'Titan', xpRequired: 22000, badge: '⚡', color: '#DC2626', perks: ['35% token discount', 'Unlock: Golden Noir bg', 'Titan frame'], tokenBonus: 2500 },
    // Elite Tier (16-20)
    { level: 16, title: 'Ascendant', xpRequired: 28000, badge: '🌠', color: '#9333EA', perks: ['Ascendant aura', 'Unlock: Cosmic Nebula bg'], tokenBonus: 3000 },
    { level: 17, title: 'Paragon', xpRequired: 35000, badge: '💎', color: '#0891B2', perks: ['40% token discount', 'Paragon badge'], tokenBonus: 3500 },
    { level: 18, title: 'Mythic', xpRequired: 45000, badge: '🐉', color: '#BE123C', perks: ['Mythic frame', 'Unlock: Obsidian Flow bg', 'All backgrounds 50% off'], tokenBonus: 4000 },
    { level: 19, title: 'Transcendent', xpRequired: 60000, badge: '🌌', color: '#4F46E5', perks: ['50% token discount', 'Transcendent effects'], tokenBonus: 5000 },
    { level: 20, title: 'Immortal', xpRequired: 80000, badge: '♾️', color: '#F59E0B', perks: ['All backgrounds unlocked', 'Immortal crown', 'Max token discount', 'Legendary status'], tokenBonus: 10000 },
];

// ==================== XP EARNING RATES ====================

export interface XPEarningConfig {
    // Basic activities
    sendMessage: number;
    receiveResponse: number;
    askComplexQuestion: number;

    // Learning activities
    completeQuiz: number;
    passQuiz: number;          // Score > 70%
    aceQuiz: number;           // Score > 90%
    completeExam: number;
    passExam: number;

    // Engagement activities
    dailyLogin: number;
    streakDay: number;         // Per day of streak
    studySession15min: number;
    studySession30min: number;
    studySession60min: number;

    // Achievement unlocks
    firstQuiz: number;
    first100Messages: number;
    firstExpertQuestion: number;

    // Social
    referFriend: number;
    shareAchievement: number;
}

export const DEFAULT_XP_CONFIG: XPEarningConfig = {
    // XP is free - keep these generous for engagement
    sendMessage: 2,
    receiveResponse: 3,
    askComplexQuestion: 5,

    completeQuiz: 15,
    passQuiz: 25,
    aceQuiz: 50,
    completeExam: 30,
    passExam: 50,

    dailyLogin: 10,
    streakDay: 5,
    studySession15min: 10,
    studySession30min: 25,
    studySession60min: 50,

    firstQuiz: 100,
    first100Messages: 150,
    firstExpertQuestion: 50,

    referFriend: 200,
    shareAchievement: 25,
};

// ==================== TOKEN REWARD SUSTAINABILITY ====================
// These values are REDUCED from original to ensure profitability
// See config/tokenEconomyConfig.ts for full analysis
export const TOKEN_REWARD_MULTIPLIER = 0.5; // Apply 50% reduction to all token rewards

// ==================== ACHIEVEMENT SYSTEM ====================

export interface Achievement {
    id: string;
    name: string;
    description: string;
    icon: string;
    category: 'learning' | 'engagement' | 'mastery' | 'social' | 'special';
    rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

    // Requirements
    requirement: {
        type: 'count' | 'streak' | 'score' | 'time' | 'special';
        metric: string;
        target: number;
    };

    // Rewards
    xpReward: number;
    tokenReward: number;

    // Visual
    color: string;
    glow: boolean;
    animation?: string;
    tier?: string;
}

export const ACHIEVEMENTS: Achievement[] = [
    // Learning Achievements
    {
        id: 'first_question',
        name: 'Curious Mind',
        description: 'Ask your first question',
        icon: '💭',
        category: 'learning',
        rarity: 'common',
        requirement: { type: 'count', metric: 'questions_asked', target: 1 },
        xpReward: 10,
        tokenReward: 25,
        color: '#10B981',
        glow: false,
    },
    {
        id: 'ten_questions',
        name: 'Getting Started',
        description: 'Ask 10 questions',
        icon: '📝',
        category: 'learning',
        rarity: 'common',
        requirement: { type: 'count', metric: 'questions_asked', target: 10 },
        xpReward: 25,
        tokenReward: 50,
        color: '#10B981',
        glow: false,
    },
    {
        id: 'hundred_questions',
        name: 'Knowledge Seeker',
        description: 'Ask 100 questions',
        icon: '🔍',
        category: 'learning',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'questions_asked', target: 100 },
        xpReward: 100,
        tokenReward: 200,
        color: '#3B82F6',
        glow: true,
    },
    {
        id: 'thousand_questions',
        name: 'Scholar',
        description: 'Ask 1,000 questions',
        icon: '📚',
        category: 'learning',
        rarity: 'rare',
        requirement: { type: 'count', metric: 'questions_asked', target: 1000 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#8B5CF6',
        glow: true,
        animation: 'sparkle',
    },

    // Quiz Achievements
    {
        id: 'first_quiz',
        name: 'Quiz Taker',
        description: 'Complete your first quiz',
        icon: '✅',
        category: 'learning',
        rarity: 'common',
        requirement: { type: 'count', metric: 'quizzes_completed', target: 1 },
        xpReward: 20,
        tokenReward: 50,
        color: '#10B981',
        glow: false,
    },
    {
        id: 'quiz_ace',
        name: 'Quiz Ace',
        description: 'Score 100% on any quiz',
        icon: '💯',
        category: 'mastery',
        rarity: 'uncommon',
        requirement: { type: 'score', metric: 'quiz_perfect', target: 1 },
        xpReward: 75,
        tokenReward: 150,
        color: '#F59E0B',
        glow: true,
    },
    {
        id: 'quiz_master',
        name: 'Quiz Master',
        description: 'Complete 50 quizzes',
        icon: '🎯',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'count', metric: 'quizzes_completed', target: 50 },
        xpReward: 300,
        tokenReward: 500,
        color: '#EC4899',
        glow: true,
        animation: 'pulse',
    },

    // Streak Achievements
    {
        id: 'streak_3',
        name: 'On a Roll',
        description: '3-day study streak',
        icon: '🔥',
        category: 'engagement',
        rarity: 'common',
        requirement: { type: 'streak', metric: 'login_streak', target: 3 },
        xpReward: 30,
        tokenReward: 75,
        color: '#EF4444',
        glow: false,
    },
    {
        id: 'streak_7',
        name: 'Weekly Warrior',
        description: '7-day study streak',
        icon: '⚡',
        category: 'engagement',
        rarity: 'uncommon',
        requirement: { type: 'streak', metric: 'login_streak', target: 7 },
        xpReward: 75,
        tokenReward: 150,
        color: '#F59E0B',
        glow: true,
    },
    {
        id: 'streak_30',
        name: 'Monthly Master',
        description: '30-day study streak',
        icon: '🏆',
        category: 'engagement',
        rarity: 'rare',
        requirement: { type: 'streak', metric: 'login_streak', target: 30 },
        xpReward: 300,
        tokenReward: 500,
        color: '#8B5CF6',
        glow: true,
        animation: 'rainbow',
    },
    {
        id: 'streak_100',
        name: 'Legendary Dedication',
        description: '100-day study streak',
        icon: '👑',
        category: 'engagement',
        rarity: 'legendary',
        requirement: { type: 'streak', metric: 'login_streak', target: 100 },
        xpReward: 1000,
        tokenReward: 2000,
        color: '#F472B6',
        glow: true,
        animation: 'fireworks',
    },

    // Domain Mastery
    {
        id: 'far_explorer',
        name: 'FAR Explorer',
        description: 'Ask 50 FAR questions',
        icon: '📊',
        category: 'mastery',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'far_questions', target: 50 },
        xpReward: 100,
        tokenReward: 200,
        color: '#3B82F6',
        glow: true,
    },
    {
        id: 'tax_wizard',
        name: 'Tax Wizard',
        description: 'Ask 50 TAX questions',
        icon: '💰',
        category: 'mastery',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'tax_questions', target: 50 },
        xpReward: 100,
        tokenReward: 200,
        color: '#F59E0B',
        glow: true,
    },
    {
        id: 'audit_expert',
        name: 'Audit Expert',
        description: 'Ask 50 AUD questions',
        icon: '🔍',
        category: 'mastery',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'aud_questions', target: 50 },
        xpReward: 100,
        tokenReward: 200,
        color: '#10B981',
        glow: true,
    },
    {
        id: 'all_rounder',
        name: 'Renaissance Accountant',
        description: 'Ask questions in all 6 domains',
        icon: '🌟',
        category: 'mastery',
        rarity: 'epic',
        requirement: { type: 'special', metric: 'all_domains', target: 6 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#EC4899',
        glow: true,
        animation: 'sparkle',
    },

    // Time-based
    {
        id: 'night_owl',
        name: 'Night Owl',
        description: 'Study between 12 AM - 4 AM',
        icon: '🦉',
        category: 'special',
        rarity: 'uncommon',
        requirement: { type: 'special', metric: 'night_study', target: 1 },
        xpReward: 50,
        tokenReward: 100,
        color: '#6366F1',
        glow: true,
    },
    {
        id: 'early_bird',
        name: 'Early Bird',
        description: 'Study before 6 AM',
        icon: '🐦',
        category: 'special',
        rarity: 'uncommon',
        requirement: { type: 'special', metric: 'early_study', target: 1 },
        xpReward: 50,
        tokenReward: 100,
        color: '#F59E0B',
        glow: true,
    },
    {
        id: 'marathon_session',
        name: 'Marathon Studier',
        description: '2-hour continuous study session',
        icon: '🏃',
        category: 'engagement',
        rarity: 'rare',
        requirement: { type: 'time', metric: 'session_minutes', target: 120 },
        xpReward: 150,
        tokenReward: 300,
        color: '#EF4444',
        glow: true,
        animation: 'pulse',
    },

    // Social
    {
        id: 'first_referral',
        name: 'Friend Maker',
        description: 'Refer your first friend',
        icon: '👥',
        category: 'social',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'referrals', target: 1 },
        xpReward: 100,
        tokenReward: 500,
        color: '#10B981',
        glow: true,
    },
    {
        id: 'influencer',
        name: 'Influencer',
        description: 'Refer 10 friends',
        icon: '🌟',
        category: 'social',
        rarity: 'epic',
        requirement: { type: 'count', metric: 'referrals', target: 10 },
        xpReward: 500,
        tokenReward: 2000,
        color: '#EC4899',
        glow: true,
        animation: 'sparkle',
    },

    // CPALE Subject Mastery
    {
        id: 'audit_ace',
        name: 'Audit Ace',
        description: 'Unlock the Auditing Mastery Path',
        icon: '🔍',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'pass_prelim_aud', target: 1 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#10B981',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'tax_titan',
        name: 'Tax Titan',
        description: 'Unlock the Taxation Mastery Path',
        icon: '💰',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'pass_prelim_tax', target: 1 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#F59E0B',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'law_legend',
        name: 'Law Legend',
        description: 'Unlock the RFBT Mastery Path',
        icon: '⚖️',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'pass_prelim_rfbt', target: 1 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#EC4899',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'far_master',
        name: 'FAR & Wide',
        description: 'Unlock the FAR Mastery Path',
        icon: '📊',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'pass_prelim_far', target: 1 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#3B82F6',
        glow: true,
        animation: 'pulse',
    },

    // Study Time Achievements
    {
        id: 'study_1h',
        name: 'Hour of Power',
        description: 'Study for 1 hour total',
        icon: '⏰',
        category: 'engagement',
        rarity: 'common',
        requirement: { type: 'time', metric: 'session_minutes', target: 60 },
        xpReward: 30,
        tokenReward: 50,
        color: '#10B981',
        glow: false,
    },
    {
        id: 'study_10h',
        name: 'Dedicated Scholar',
        description: 'Study for 10 hours total',
        icon: '📚',
        category: 'engagement',
        rarity: 'uncommon',
        requirement: { type: 'time', metric: 'session_minutes', target: 600 },
        xpReward: 150,
        tokenReward: 300,
        color: '#3B82F6',
        glow: true,
    },
    {
        id: 'study_50h',
        name: 'Iron Will',
        description: 'Study for 50 hours total',
        icon: '💪',
        category: 'engagement',
        rarity: 'rare',
        requirement: { type: 'time', metric: 'session_minutes', target: 3000 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#8B5CF6',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'study_100h',
        name: 'Centurion',
        description: 'Study for 100 hours total',
        icon: '🏅',
        category: 'engagement',
        rarity: 'epic',
        requirement: { type: 'time', metric: 'session_minutes', target: 6000 },
        xpReward: 1500,
        tokenReward: 3000,
        color: '#EC4899',
        glow: true,
        animation: 'fireworks',
    },

    // Session Milestones
    {
        id: 'sessions_10',
        name: 'Regular',
        description: 'Start 10 study sessions',
        icon: '📅',
        category: 'engagement',
        rarity: 'common',
        requirement: { type: 'count', metric: 'sessions_started', target: 10 },
        xpReward: 25,
        tokenReward: 50,
        color: '#10B981',
        glow: false,
    },
    {
        id: 'sessions_50',
        name: 'Habitual Learner',
        description: 'Start 50 study sessions',
        icon: '📈',
        category: 'engagement',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'sessions_started', target: 50 },
        xpReward: 100,
        tokenReward: 200,
        color: '#3B82F6',
        glow: true,
    },
    {
        id: 'sessions_200',
        name: 'Session Veteran',
        description: 'Start 200 study sessions',
        icon: '🎖️',
        category: 'engagement',
        rarity: 'rare',
        requirement: { type: 'count', metric: 'sessions_started', target: 200 },
        xpReward: 400,
        tokenReward: 800,
        color: '#8B5CF6',
        glow: true,
        animation: 'sparkle',
    },

    // Question Milestones (extended)
    {
        id: 'five_thousand_questions',
        name: 'Inquisitor',
        description: 'Ask 5,000 questions',
        icon: '🔭',
        category: 'learning',
        rarity: 'epic',
        requirement: { type: 'count', metric: 'questions_asked', target: 5000 },
        xpReward: 2000,
        tokenReward: 3000,
        color: '#EC4899',
        glow: true,
        animation: 'fireworks',
    },

    // Quiz Milestones (extended)
    {
        id: 'quiz_100',
        name: 'Quiz Legend',
        description: 'Complete 100 quizzes',
        icon: '👑',
        category: 'mastery',
        rarity: 'epic',
        requirement: { type: 'count', metric: 'quizzes_completed', target: 100 },
        xpReward: 800,
        tokenReward: 1500,
        color: '#F59E0B',
        glow: true,
        animation: 'fireworks',
    },
    {
        id: 'perfect_5',
        name: 'Perfectionist',
        description: 'Score 100% on 5 quizzes',
        icon: '🎯',
        category: 'mastery',
        rarity: 'rare',
        requirement: { type: 'score', metric: 'quiz_perfect', target: 5 },
        xpReward: 200,
        tokenReward: 400,
        color: '#10B981',
        glow: true,
        animation: 'sparkle',
    },
    {
        id: 'perfect_20',
        name: 'Flawless',
        description: 'Score 100% on 20 quizzes',
        icon: '💎',
        category: 'mastery',
        rarity: 'epic',
        requirement: { type: 'score', metric: 'quiz_perfect', target: 20 },
        xpReward: 1000,
        tokenReward: 2000,
        color: '#8B5CF6',
        glow: true,
        animation: 'fireworks',
    },

    // Extended Streak Achievements
    {
        id: 'streak_14',
        name: 'Fortnight Focus',
        description: '14-day study streak',
        icon: '🔥',
        category: 'engagement',
        rarity: 'uncommon',
        requirement: { type: 'streak', metric: 'login_streak', target: 14 },
        xpReward: 150,
        tokenReward: 300,
        color: '#EF4444',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'streak_60',
        name: 'Two-Month Titan',
        description: '60-day study streak',
        icon: '💪',
        category: 'engagement',
        rarity: 'epic',
        requirement: { type: 'streak', metric: 'login_streak', target: 60 },
        xpReward: 600,
        tokenReward: 1200,
        color: '#EC4899',
        glow: true,
        animation: 'fireworks',
    },
    {
        id: 'streak_365',
        name: 'Year of Mastery',
        description: '365-day study streak',
        icon: '🌟',
        category: 'engagement',
        rarity: 'legendary',
        requirement: { type: 'streak', metric: 'login_streak', target: 365 },
        xpReward: 5000,
        tokenReward: 10000,
        color: '#F59E0B',
        glow: true,
        animation: 'fireworks',
    },

    // Social Achievements (extended)
    {
        id: 'referral_5',
        name: 'Recruiter',
        description: 'Refer 5 friends',
        icon: '🤝',
        category: 'social',
        rarity: 'rare',
        requirement: { type: 'count', metric: 'referrals', target: 5 },
        xpReward: 300,
        tokenReward: 1000,
        color: '#10B981',
        glow: true,
        animation: 'sparkle',
    },
    {
        id: 'referral_25',
        name: 'Ambassador',
        description: 'Refer 25 friends',
        icon: '🌟',
        category: 'social',
        rarity: 'legendary',
        requirement: { type: 'count', metric: 'referrals', target: 25 },
        xpReward: 2000,
        tokenReward: 5000,
        color: '#F59E0B',
        glow: true,
        animation: 'fireworks',
    },

    // Purchase-related (encourage spending)
    {
        id: 'first_purchase',
        name: 'Supporter',
        description: 'Make your first purchase',
        icon: '💎',
        category: 'special',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'purchases', target: 1 },
        xpReward: 100,
        tokenReward: 100,
        color: '#8B5CF6',
        glow: true,
    },
    {
        id: 'premium_member',
        name: 'Premium Member',
        description: 'Upgrade to Premium',
        icon: '⭐',
        category: 'special',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'premium', target: 1 },
        xpReward: 200,
        tokenReward: 500,
        color: '#F59E0B',
        glow: true,
        animation: 'glow',
    },
    {
        id: 'max_member',
        name: 'MAX Elite',
        description: 'Upgrade to MAX',
        icon: '👑',
        category: 'special',
        rarity: 'legendary',
        requirement: { type: 'special', metric: 'max', target: 1 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#EC4899',
        glow: true,
        animation: 'fireworks',
    },

    // Hidden / Special Achievements
    {
        id: 'weekend_warrior',
        name: 'Weekend Warrior',
        description: 'Study on both Saturday and Sunday',
        icon: '🏄',
        category: 'special',
        rarity: 'uncommon',
        requirement: { type: 'special', metric: 'weekend_study', target: 1 },
        xpReward: 75,
        tokenReward: 150,
        color: '#06B6D4',
        glow: true,
    },
    {
        id: 'speed_demon',
        name: 'Speed Demon',
        description: 'Ask 20 questions in under 10 minutes',
        icon: '🚀',
        category: 'special',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'rapid_questions', target: 20 },
        xpReward: 150,
        tokenReward: 300,
        color: '#EF4444',
        glow: true,
        animation: 'pulse',
    },
    {
        id: 'background_collector',
        name: 'Collector',
        description: 'Unlock 5 chat backgrounds',
        icon: '🎨',
        category: 'special',
        rarity: 'uncommon',
        requirement: { type: 'count', metric: 'backgrounds_unlocked', target: 5 },
        xpReward: 100,
        tokenReward: 200,
        color: '#8B5CF6',
        glow: true,
    },
    {
        id: 'background_connoisseur',
        name: 'Connoisseur',
        description: 'Unlock all chat backgrounds',
        icon: '🖼️',
        category: 'special',
        rarity: 'legendary',
        requirement: { type: 'special', metric: 'all_backgrounds', target: 1 },
        xpReward: 1000,
        tokenReward: 2000,
        color: '#EC4899',
        glow: true,
        animation: 'fireworks',
    },
    {
        id: 'level_10',
        name: 'Legendary Status',
        description: 'Reach Level 10',
        icon: '🌟',
        category: 'engagement',
        rarity: 'rare',
        requirement: { type: 'special', metric: 'reach_level', target: 10 },
        xpReward: 500,
        tokenReward: 1000,
        color: '#F59E0B',
        glow: true,
        animation: 'fireworks',
    },
    {
        id: 'level_20',
        name: 'Immortal Ascension',
        description: 'Reach Level 20',
        icon: '♾️',
        category: 'engagement',
        rarity: 'legendary',
        requirement: { type: 'special', metric: 'reach_level', target: 20 },
        xpReward: 5000,
        tokenReward: 10000,
        color: '#F59E0B',
        glow: true,
        animation: 'fireworks',
    },
];

// ==================== DAILY CHALLENGES ====================

export interface DailyChallenge {
    id: string;
    name: string;
    description: string;
    icon: string;

    // Requirements
    type: 'questions' | 'quizzes' | 'time' | 'domain' | 'streak';
    target: number;

    // Rewards
    xpReward: number;
    tokenReward: number;

    // Difficulty affects reward multiplier
    difficulty: 'easy' | 'medium' | 'hard';
}

export const DAILY_CHALLENGES: DailyChallenge[] = [
    // Easy challenges
    {
        id: 'daily_3_questions',
        name: 'Warm Up',
        description: 'Ask 3 questions today',
        icon: '💬',
        type: 'questions',
        target: 3,
        xpReward: 15,
        tokenReward: 25,
        difficulty: 'easy',
    },
    {
        id: 'daily_login_streak',
        name: 'Keep the Streak',
        description: 'Log in today',
        icon: '🔥',
        type: 'streak',
        target: 1,
        xpReward: 10,
        tokenReward: 20,
        difficulty: 'easy',
    },
    {
        id: 'daily_5_questions',
        name: 'Five Alive',
        description: 'Ask 5 questions today',
        icon: '✋',
        type: 'questions',
        target: 5,
        xpReward: 20,
        tokenReward: 35,
        difficulty: 'easy',
    },
    {
        id: 'daily_10min',
        name: 'Quick Study',
        description: 'Study for 10 minutes today',
        icon: '⏳',
        type: 'time',
        target: 10,
        xpReward: 15,
        tokenReward: 25,
        difficulty: 'easy',
    },
    {
        id: 'daily_1_session',
        name: 'Show Up',
        description: 'Start a study session today',
        icon: '💻',
        type: 'streak',
        target: 1,
        xpReward: 10,
        tokenReward: 15,
        difficulty: 'easy',
    },

    // Medium challenges
    {
        id: 'daily_10_questions',
        name: 'Knowledge Hunter',
        description: 'Ask 10 questions today',
        icon: '🎯',
        type: 'questions',
        target: 10,
        xpReward: 40,
        tokenReward: 75,
        difficulty: 'medium',
    },
    {
        id: 'daily_quiz',
        name: 'Quiz Time',
        description: 'Complete 1 quiz today',
        icon: '✅',
        type: 'quizzes',
        target: 1,
        xpReward: 30,
        tokenReward: 50,
        difficulty: 'medium',
    },
    {
        id: 'daily_30min',
        name: 'Focused Study',
        description: 'Study for 30 minutes today',
        icon: '⏱️',
        type: 'time',
        target: 30,
        xpReward: 35,
        tokenReward: 60,
        difficulty: 'medium',
    },
    {
        id: 'daily_15_questions',
        name: 'Curious Cat',
        description: 'Ask 15 questions today',
        icon: '🐱',
        type: 'questions',
        target: 15,
        xpReward: 50,
        tokenReward: 85,
        difficulty: 'medium',
    },
    {
        id: 'daily_2_quizzes',
        name: 'Double Quiz',
        description: 'Complete 2 quizzes today',
        icon: '✅✅',
        type: 'quizzes',
        target: 2,
        xpReward: 50,
        tokenReward: 90,
        difficulty: 'medium',
    },
    {
        id: 'daily_2_domains',
        name: 'Cross-Study',
        description: 'Ask questions in 2 different domains',
        icon: '🔀',
        type: 'domain',
        target: 2,
        xpReward: 40,
        tokenReward: 70,
        difficulty: 'medium',
    },
    {
        id: 'daily_45min',
        name: 'Deep Focus',
        description: 'Study for 45 minutes today',
        icon: '🧠',
        type: 'time',
        target: 45,
        xpReward: 50,
        tokenReward: 85,
        difficulty: 'medium',
    },

    // Hard challenges
    {
        id: 'daily_25_questions',
        name: 'Deep Dive',
        description: 'Ask 25 questions today',
        icon: '🚀',
        type: 'questions',
        target: 25,
        xpReward: 100,
        tokenReward: 200,
        difficulty: 'hard',
    },
    {
        id: 'daily_3_quizzes',
        name: 'Quiz Champion',
        description: 'Complete 3 quizzes today',
        icon: '🏆',
        type: 'quizzes',
        target: 3,
        xpReward: 80,
        tokenReward: 150,
        difficulty: 'hard',
    },
    {
        id: 'daily_60min',
        name: 'Marathon Study',
        description: 'Study for 60 minutes today',
        icon: '💪',
        type: 'time',
        target: 60,
        xpReward: 75,
        tokenReward: 125,
        difficulty: 'hard',
    },
    {
        id: 'daily_multi_domain',
        name: 'Well-Rounded',
        description: 'Ask questions in 3 different domains',
        icon: '🌈',
        type: 'domain',
        target: 3,
        xpReward: 60,
        tokenReward: 100,
        difficulty: 'hard',
    },
    {
        id: 'daily_40_questions',
        name: 'Unstoppable',
        description: 'Ask 40 questions today',
        icon: '🔥',
        type: 'questions',
        target: 40,
        xpReward: 150,
        tokenReward: 300,
        difficulty: 'hard',
    },
    {
        id: 'daily_5_quizzes',
        name: 'Quiz Maniac',
        description: 'Complete 5 quizzes today',
        icon: '🤯',
        type: 'quizzes',
        target: 5,
        xpReward: 120,
        tokenReward: 250,
        difficulty: 'hard',
    },
    {
        id: 'daily_90min',
        name: 'Endurance Test',
        description: 'Study for 90 minutes today',
        icon: '🏃',
        type: 'time',
        target: 90,
        xpReward: 100,
        tokenReward: 200,
        difficulty: 'hard',
    },
    {
        id: 'daily_4_domains',
        name: 'Renaissance',
        description: 'Ask questions in 4 different domains',
        icon: '🌟',
        type: 'domain',
        target: 4,
        xpReward: 100,
        tokenReward: 175,
        difficulty: 'hard',
    },
];

// ==================== SURPRISE REWARDS ====================

export interface SurpriseReward {
    id: string;
    name: string;
    message: string;
    icon: string;

    // Trigger conditions
    trigger: 'random' | 'milestone' | 'time' | 'behavior';
    probability?: number; // For random triggers (0-1)
    condition?: string;   // For conditional triggers

    // Rewards
    minTokens: number;
    maxTokens: number;
    xpReward: number;

    // Visual
    animation: string;
    color: string;
}

export const SURPRISE_REWARDS: SurpriseReward[] = [
    {
        id: 'lucky_bonus',
        name: 'Lucky Bonus!',
        message: '🎰 You got a lucky bonus!',
        icon: '🎰',
        trigger: 'random',
        probability: 0.02, // 2% chance per message
        minTokens: 10,
        maxTokens: 100,
        xpReward: 10,
        animation: 'confetti',
        color: '#F59E0B',
    },
    {
        id: 'study_hero',
        name: 'Study Hero!',
        message: '🦸 You\'re on fire! Here\'s a bonus!',
        icon: '🦸',
        trigger: 'behavior',
        condition: '10_questions_no_break',
        minTokens: 50,
        maxTokens: 150,
        xpReward: 25,
        animation: 'fireworks',
        color: '#EF4444',
    },
    {
        id: 'night_bonus',
        name: 'Night Owl Bonus',
        message: '🌙 Studying late? Here\'s extra motivation!',
        icon: '🌙',
        trigger: 'time',
        condition: 'hour_23_to_4',
        minTokens: 25,
        maxTokens: 75,
        xpReward: 15,
        animation: 'sparkle',
        color: '#6366F1',
    },
    {
        id: 'comeback_bonus',
        name: 'Welcome Back!',
        message: '👋 We missed you! Here\'s a comeback bonus!',
        icon: '👋',
        trigger: 'behavior',
        condition: 'returned_after_3_days',
        minTokens: 100,
        maxTokens: 200,
        xpReward: 50,
        animation: 'confetti',
        color: '#10B981',
    },
    {
        id: 'persistence_bonus',
        name: 'Persistence Pays!',
        message: '💪 You asked a tough question! Bonus earned!',
        icon: '💪',
        trigger: 'behavior',
        condition: 'asked_expert_question',
        minTokens: 20,
        maxTokens: 50,
        xpReward: 15,
        animation: 'pulse',
        color: '#8B5CF6',
    },
    {
        id: 'streak_saver',
        name: 'Streak Saver!',
        message: '🔥 You almost lost your streak! Saved just in time!',
        icon: '🔥',
        trigger: 'behavior',
        condition: 'login_before_streak_expires',
        minTokens: 30,
        maxTokens: 100,
        xpReward: 20,
        animation: 'pulse',
        color: '#EF4444',
    },
];

// ==================== FOMO ELEMENTS ====================

export interface FOMOElement {
    id: string;
    type: 'limited_time' | 'social_proof' | 'scarcity' | 'countdown';

    // Display
    message: string;
    icon: string;
    color: string;

    // Timing
    showAfterMinutes?: number;
    displayDurationSeconds?: number;
    cooldownMinutes?: number;
}

export const FOMO_ELEMENTS: FOMOElement[] = [
    {
        id: 'users_online',
        type: 'social_proof',
        message: '🟢 {count} students studying right now',
        icon: '🟢',
        color: '#10B981',
    },
    {
        id: 'recent_upgrade',
        type: 'social_proof',
        message: '⭐ Someone just upgraded to Premium!',
        icon: '⭐',
        color: '#F59E0B',
        cooldownMinutes: 30,
    },
    {
        id: 'flash_sale',
        type: 'limited_time',
        message: '⚡ Flash Sale! 30% off tokens - ends in {time}',
        icon: '⚡',
        color: '#EF4444',
        displayDurationSeconds: 300,
    },
    {
        id: 'streak_warning',
        type: 'countdown',
        message: '🔥 Your streak expires in {time}! Don\'t lose it!',
        icon: '🔥',
        color: '#EF4444',
    },
    {
        id: 'limited_bonus',
        type: 'scarcity',
        message: '🎁 Only {count} bonus packages left today!',
        icon: '🎁',
        color: '#8B5CF6',
    },
    {
        id: 'popular_topic',
        type: 'social_proof',
        message: '📈 {topic} is trending! {count} students learning it now',
        icon: '📈',
        color: '#3B82F6',
    },
];

// ==================== PROGRESS MILESTONES ====================

export interface ProgressMilestone {
    id: string;
    name: string;
    description: string;
    icon: string;

    // Threshold
    metric: string;
    threshold: number;

    // Celebration
    celebration: {
        title: string;
        message: string;
        animation: string;
        confetti: boolean;
        sound?: string;
    };

    // Rewards
    tokenReward: number;
    xpReward: number;
    badgeUnlock?: string;
}

export const PROGRESS_MILESTONES: ProgressMilestone[] = [
    {
        id: 'first_session',
        name: 'First Steps',
        description: 'Complete your first study session',
        icon: '🎉',
        metric: 'sessions_completed',
        threshold: 1,
        celebration: {
            title: 'Welcome to Luca!',
            message: 'You\'ve completed your first study session! 🎉',
            animation: 'confetti',
            confetti: true,
        },
        tokenReward: 50,
        xpReward: 25,
    },
    {
        id: 'question_10',
        name: '10 Questions',
        description: 'Asked 10 questions',
        icon: '🔟',
        metric: 'total_questions',
        threshold: 10,
        celebration: {
            title: 'Getting Curious!',
            message: 'You\'ve asked 10 questions! Keep learning! 📚',
            animation: 'sparkle',
            confetti: false,
        },
        tokenReward: 25,
        xpReward: 15,
    },
    {
        id: 'question_50',
        name: '50 Questions',
        description: 'Asked 50 questions',
        icon: '5️⃣0️⃣',
        metric: 'total_questions',
        threshold: 50,
        celebration: {
            title: 'Knowledge Seeker!',
            message: '50 questions! You\'re really diving deep! 🏊',
            animation: 'fireworks',
            confetti: true,
        },
        tokenReward: 100,
        xpReward: 50,
        badgeUnlock: 'knowledge_seeker',
    },
    {
        id: 'question_100',
        name: '100 Questions',
        description: 'Asked 100 questions',
        icon: '💯',
        metric: 'total_questions',
        threshold: 100,
        celebration: {
            title: 'Century Club!',
            message: '100 questions! You\'re unstoppable! 🚀',
            animation: 'fireworks',
            confetti: true,
        },
        tokenReward: 250,
        xpReward: 100,
        badgeUnlock: 'century_club',
    },
    {
        id: 'study_1_hour',
        name: '1 Hour Study',
        description: 'Studied for 1 hour total',
        icon: '⏰',
        metric: 'total_study_minutes',
        threshold: 60,
        celebration: {
            title: 'Hour of Power!',
            message: 'You\'ve studied for a full hour! Time well spent! 💪',
            animation: 'pulse',
            confetti: false,
        },
        tokenReward: 50,
        xpReward: 30,
    },
    {
        id: 'study_10_hours',
        name: '10 Hours Study',
        description: 'Studied for 10 hours total',
        icon: '📖',
        metric: 'total_study_minutes',
        threshold: 600,
        celebration: {
            title: 'Dedicated Learner!',
            message: '10 hours of study! You\'re committed! 🏆',
            animation: 'fireworks',
            confetti: true,
        },
        tokenReward: 300,
        xpReward: 150,
        badgeUnlock: 'dedicated_learner',
    },
    {
        id: 'quiz_10',
        name: '10 Quizzes',
        description: 'Completed 10 quizzes',
        icon: '✅',
        metric: 'quizzes_completed',
        threshold: 10,
        celebration: {
            title: 'Quiz Champion!',
            message: '10 quizzes completed! Practice makes perfect! 🎯',
            animation: 'sparkle',
            confetti: true,
        },
        tokenReward: 150,
        xpReward: 75,
    },
];

// ==================== GAMIFICATION CONFIG ====================

// ==================== GAMIFICATION CONFIG ====================

// ==================== GAMIFICATION CONFIG ====================

export interface GamificationConfig {
    enabled: boolean;

    features: {
        xpSystem: boolean;
        levelSystem: boolean;
        achievements: boolean;
        dailyChallenges: boolean;
        streaks: boolean;
        surpriseRewards: boolean;
        fomoElements: boolean;
        milestones: boolean;
        leaderboard: boolean;
    };

    xpRates: {
        sendMessage: number;
        receiveResponse: number;
        askComplexQuestion: number;
        completeQuiz: number;
        passQuiz: number;
        aceQuiz: number;
        completeExam: number;
        passExam: number;
        dailyLogin: number;
        streakDay: number;
        studySession15min: number;
        studySession30min: number;
        studySession60min: number;
        referFriend: number;
    };

    multipliers: {
        premiumXPBonus: number;
        maxXPBonus: number;
        streakXPBonus: number;
        weekendBonus: number;
    };

    levelConfig: {
        maxLevel: number;
        xpScalingFactor: number;
        tokenBonusPerLevel: number;
    };

    streakConfig: {
        enabled: boolean;
        dailyBonusBase: number;
        dailyBonusIncrement: number;
        maxDailyBonus: number;
        freezeEnabled: boolean;
        freezeCost: number;
        expirationHours: number;
    };

    achievementConfig: {
        enabled: boolean;
        tokenMultiplier: number;
        xpMultiplier: number;
        showCelebration: boolean;
        celebrationDuration: number;
    };

    dailyChallengeConfig: {
        enabled: boolean;
        challengesPerDay: number;
        easyRewardTokens: number;
        mediumRewardTokens: number;
        hardRewardTokens: number;
        completionBonus: number;
        resetHour: number;
    };

    challengesPool: DailyChallenge[];

    surpriseRewardConfig: {
        enabled: boolean;
        baseChance: number;
        minTokens: number;
        maxTokens: number;
        cooldownMinutes: number;
    };

    fomoConfig: {
        enabled: boolean;
        showOnlineCount: boolean;
        showRecentActivity: boolean;
        showFlashSales: boolean;
        streakWarningHours: number;
    };

    tierAccess: {
        standard: boolean;
        premium: boolean;
        max: boolean;
    };
}

export const DEFAULT_GAMIFICATION_CONFIG: GamificationConfig = {
    enabled: true,

    features: {
        xpSystem: true,
        levelSystem: true,
        achievements: true,
        dailyChallenges: true,
        streaks: true,
        surpriseRewards: true,
        fomoElements: true,
        milestones: true,
        leaderboard: false,
    },

    xpRates: {
        sendMessage: 2,
        receiveResponse: 3,
        askComplexQuestion: 5,
        completeQuiz: 15,
        passQuiz: 25,
        aceQuiz: 50,
        completeExam: 30,
        passExam: 50,
        dailyLogin: 10,
        streakDay: 5,
        studySession15min: 10,
        studySession30min: 25,
        studySession60min: 50,
        referFriend: 200,
    },

    multipliers: {
        premiumXPBonus: 1.5,
        maxXPBonus: 2.0,
        streakXPBonus: 0.1,
        weekendBonus: 1.25,
    },

    levelConfig: {
        maxLevel: 20,
        xpScalingFactor: 1.5,
        tokenBonusPerLevel: 50,
    },

    streakConfig: {
        enabled: true,
        dailyBonusBase: 25,
        dailyBonusIncrement: 5,
        maxDailyBonus: 100,
        freezeEnabled: true,
        freezeCost: 500,
        expirationHours: 24,
    },

    achievementConfig: {
        enabled: true,
        tokenMultiplier: 1.0,
        xpMultiplier: 1.0,
        showCelebration: true,
        celebrationDuration: 5000,
    },

    dailyChallengeConfig: {
        enabled: true,
        challengesPerDay: 3,
        easyRewardTokens: 25,
        mediumRewardTokens: 50,
        hardRewardTokens: 100,
        completionBonus: 50,
        resetHour: 0,
    },

    challengesPool: DAILY_CHALLENGES,

    surpriseRewardConfig: {
        enabled: true,
        baseChance: 2,
        minTokens: 10,
        maxTokens: 100,
        cooldownMinutes: 30,
    },

    fomoConfig: {
        enabled: true,
        showOnlineCount: true,
        showRecentActivity: true,
        showFlashSales: true,
        streakWarningHours: 6,
    },

    tierAccess: {
        standard: true,
        premium: true,
        max: true,
    },
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Calculate level from XP
 */
export function calculateLevel(xp: number): LevelConfig {
    for (let i = LEVEL_CONFIGS.length - 1; i >= 0; i--) {
        if (xp >= LEVEL_CONFIGS[i].xpRequired) {
            return LEVEL_CONFIGS[i];
        }
    }
    return LEVEL_CONFIGS[0];
}

/**
 * Calculate XP needed for next level
 */
export function xpToNextLevel(currentXP: number): { current: number; needed: number; progress: number } {
    const currentLevel = calculateLevel(currentXP);
    const nextLevelIndex = Math.min(currentLevel.level, LEVEL_CONFIGS.length - 1);
    const nextLevel = LEVEL_CONFIGS[nextLevelIndex];

    if (currentLevel.level === LEVEL_CONFIGS.length) {
        return { current: currentXP, needed: currentXP, progress: 100 };
    }

    const xpInCurrentLevel = currentXP - currentLevel.xpRequired;
    const xpNeededForNext = nextLevel.xpRequired - currentLevel.xpRequired;
    const progress = Math.round((xpInCurrentLevel / xpNeededForNext) * 100);

    return {
        current: xpInCurrentLevel,
        needed: xpNeededForNext,
        progress: Math.min(progress, 100),
    };
}

/**
 * Get random daily challenges
 */
export function getRandomDailyChallenges(count: number = 3, pool: DailyChallenge[] = DAILY_CHALLENGES): DailyChallenge[] {
    const shuffled = [...pool].sort(() => Math.random() - 0.5);
    // Ensure mix of difficulties if possible
    const easy = shuffled.filter(c => c.difficulty === 'easy').slice(0, 1);
    const medium = shuffled.filter(c => c.difficulty === 'medium').slice(0, 1);
    const hard = shuffled.filter(c => c.difficulty === 'hard').slice(0, 1);

    // If we have enough variety, use it. Otherwise just take random ones.
    if (easy.length + medium.length + hard.length >= 3 && count >= 3) {
        const remaining = shuffled.filter(c => !easy.includes(c) && !medium.includes(c) && !hard.includes(c));
        return [...easy, ...medium, ...hard, ...remaining].slice(0, count);
    }

    return shuffled.slice(0, count);
}

/**
 * Check if surprise reward should trigger
 */
export function shouldTriggerSurprise(surprise: SurpriseReward): boolean {
    if (surprise.trigger === 'random' && surprise.probability) {
        return Math.random() < surprise.probability;
    }
    return false;
}

/**
 * Get random token amount from range
 */
export function getRandomReward(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const gamificationConfig = {
    LEVEL_CONFIGS,
    ACHIEVEMENTS,
    DAILY_CHALLENGES,
    SURPRISE_REWARDS,
    FOMO_ELEMENTS,
    PROGRESS_MILESTONES,
    DEFAULT_GAMIFICATION_CONFIG,
    calculateLevel,
    xpToNextLevel,
    getRandomDailyChallenges,
    shouldTriggerSurprise,
    getRandomReward,
};

export default gamificationConfig;

